// utils/useSafeLoaderData.ts
import { useLoaderData, useLocation } from "@remix-run/react";
import { captureException } from "@sentry/remix";
import { useEffect } from "react";
import { z } from "zod/v4";

import { useNavigate } from "@shared/react-router-dom/react-router-dom";

const LOADER_RELOAD_PARAM = "loaderReload";

const SAFE_LOADER_ERROR_CODES = {
  conflict: 409,
  validationError: 422,
} as const;

/**
 * A hook that safely validates remix dataloader data against a route-defined Zod schema.
 *
 * This hook helps ensure type safety between backend loader data and frontend components
 * by validating the data against a Zod schema. If validation fails, it automatically
 * triggers a hard refresh in hopes to re-align server response and client bundle expectations.
 *
 * @template T - The type of dataloader data expected after validation
 * @param {z.ZodType<T>} schema - The Zod schema to validate the loader data against
 * @param {Object} options - Optional configuration object
 * @returns {T} The validated data matching the schema
 *
 * @throws {Error} Throws an error if initial validation fails forcing a refresh, caught by the error boundary
 *
 * @example
 * // Define your schema in a ./schema.ts file
 * const mySchema = z.object({
 *   user: z.object({
 *     id: z.number(),
 *     name: z.string(),
 *   }),
 * });
 *
 * // Use in your route component
 * function MyRouteComponent() {
 *   const data = useSafeLoaderData(mySchema);
 *   if (!data) {
 *     return <div>Refreshing...</div>;
 *   }
 *
 *   // data is now fully typed and guaranteed to match the schema
 *   return <div>{data.user.name}</div>;
 * }
 */
const useSafeLoaderData = <T>(schema: z.ZodType<T>): T => {
  const loaderData = useLoaderData<T>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const hasForcedReloaded = searchParams.get(LOADER_RELOAD_PARAM) !== null;

  const parsedResult = schema.safeParse(loaderData);
  const isSuccessfulReload = hasForcedReloaded && parsedResult.success;

  useEffect(() => {
    if (!isSuccessfulReload) {
      return;
    }

    // TODO: Remove this sentry capture once we are satisfied that this solution is working
    captureException(
      new TypeError(
        `Remix dataloader validation issue caught and successfully refreshed:  ${location.pathname}`
      ),
      {
        level: "warning",
        tags: {
          component: "useSafeLoaderData",
        },
      }
    );

    // Cleanup the loaderReload param from the url
    const params = new URLSearchParams(location.search);
    params.delete(LOADER_RELOAD_PARAM);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [isSuccessfulReload, location, navigate]);

  const createValidationError = () => {
    const message = hasForcedReloaded
      ? `Remix dataloader validation failure: ${location.pathname}`
      : `Remix dataloader validation issue caught... reloading:  ${location.pathname}`;

    const validationError = new TypeError(message);

    Object.assign(validationError, {
      code: "DATA_LOADER_MISMATCH",
      status: hasForcedReloaded
        ? SAFE_LOADER_ERROR_CODES.validationError
        : SAFE_LOADER_ERROR_CODES.conflict,
    });

    return validationError;
  };

  const forceReloadWithCacheBusting = () => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set(LOADER_RELOAD_PARAM, Date.now().toString());
      window.location.replace(url.toString());
    }
  };

  if (!parsedResult.success) {
    const error = createValidationError();

    if (!hasForcedReloaded) {
      // Trigger reload immediately after starting error capture
      forceReloadWithCacheBusting();

      // Throw the conflict error so it can be caught by the error boundary
      throw error;
    }

    // TODO: Shouldn't need this capture here, but just in case for now. Can cleanup if error boundary is capturing properly.
    captureException(error, {
      level: hasForcedReloaded ? "error" : "warning",
      tags: {
        component: "useSafeLoaderData",
      },
      extra: {
        zodErrors: JSON.stringify(z.treeifyError(parsedResult.error)),
        loaderData,
      },
    });

    // Fallback to returning the unvalidated raw loader data, casted to the expected type
    return loaderData as T;
  }

  // Return the zod validated data
  return parsedResult.data;
};

export { useSafeLoaderData, LOADER_RELOAD_PARAM, SAFE_LOADER_ERROR_CODES };
